
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Income, Expense, Student, CashSummary, Transaction, User } from './types';
import { apiService } from './services/api';
import { getFinancialInsights } from './services/gemini';
import StatCard from './components/StatCard';
import TransactionForm from './components/TransactionForm';
import StudentForm from './components/StudentForm';
import TransactionList from './components/TransactionList';
import LoginForm from './components/LoginForm';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const App: React.FC = () => {
  const [pemasukan, setPemasukan] = useState<Income[]>([]);
  const [pengeluaran, setPengeluaran] = useState<Expense[]>([]);
  const [siswa, setSiswa] = useState<Student[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const [activeAdminTab, setActiveAdminTab] = useState<'TRANSACTION' | 'STUDENT'>('TRANSACTION');
  const [aiInsight, setAiInsight] = useState<string | null>(null);
  const [isAiLoading, setIsAiLoading] = useState(false);

  const [notification, setNotification] = useState<{ message: string, type: 'success' | 'error' } | null>(null);

  const showNotification = (message: string, type: 'success' | 'error' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => {
      setNotification(null);
    }, 3500);
  };

  const sanitizeNumber = (val: any): number => {
    if (typeof val === 'number') return isNaN(val) ? 0 : val;
    if (!val) return 0;
    
    let str = String(val).trim();
    str = str.replace(/Rp|IDR|\s/gi, '');
    if (str.includes(',')) {
      str = str.split(',')[0];
    }
    str = str.replace(/\./g, '');
    const num = parseFloat(str);
    return isNaN(num) ? 0 : num;
  };

  const getProp = (obj: any, ...keys: string[]) => {
    for (const key of keys) {
      if (obj[key] !== undefined) return obj[key];
      if (obj[key.toLowerCase()] !== undefined) return obj[key.toLowerCase()];
      const snake = key.replace(/\s/g, '_');
      if (obj[snake] !== undefined) return obj[snake];
      if (obj[snake.toLowerCase()] !== undefined) return obj[snake.toLowerCase()];
    }
    return undefined;
  };

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await apiService.getAllData();
      setPemasukan(data.pemasukan || []);
      setPengeluaran(data.pengeluaran || []);
      setSiswa(data.siswa || []);
    } catch (error) {
      console.error("Gagal sinkronisasi data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleLogin = async (user: string, pass: string) => {
    setIsLoggingIn(true);
    setLoginError(null);
    const result = await apiService.login(user, pass);
    if (result.success && result.data) {
      setCurrentUser(result.data);
      setShowLoginModal(false);
      showNotification(`Selamat datang, ${result.data.username}!`, 'success');
    } else {
      setLoginError(result.message || 'Login gagal.');
    }
    setIsLoggingIn(false);
  };

  const handleLogout = () => {
    if (window.confirm("Apakah Anda yakin ingin keluar?")) {
      setCurrentUser(null);
      showNotification('Berhasil logout.', 'success');
    }
  };

  const summary: CashSummary = {
    totalIncome: pemasukan.reduce((acc, t) => acc + sanitizeNumber(getProp(t, 'Jumlah', 'jumlah')), 0),
    totalExpense: pengeluaran.reduce((acc, t) => acc + sanitizeNumber(getProp(t, 'Jumlah', 'jumlah')), 0),
    balance: 0
  };
  summary.balance = summary.totalIncome - summary.totalExpense;

  const calculateDebtInfo = useCallback((s: Student) => {
    const now = new Date();
    const currentMonthIdx = now.getMonth(); 
    
    const schoolStartMonth = 6;
    let mandatoryMonthsPassed = 0;

    if (currentMonthIdx >= schoolStartMonth) {
      mandatoryMonthsPassed = (currentMonthIdx - schoolStartMonth) + 1;
    } else {
      mandatoryMonthsPassed = (11 - schoolStartMonth + 1) + (currentMonthIdx + 1);
    }

    let iuranValue = sanitizeNumber(getProp(s, 'Iuran', 'iuran', 'Iuran_Kas'));
    if (iuranValue === 0) iuranValue = 20000; 
    
    const targetTotal = iuranValue * mandatoryMonthsPassed;
    const studentNameFromSiswa = String(getProp(s, 'Nama', 'nama', 'Nama_Siswa') || "").trim().toLowerCase();
    
    const totalPaid = pemasukan
      .filter(p => {
        const nameInIncome = String(getProp(p, 'Nama_Siswa', 'nama_siswa', 'Nama_Siswa', 'Nama', 'nama') || "").trim().toLowerCase();
        return nameInIncome === studentNameFromSiswa && studentNameFromSiswa !== "";
      })
      .reduce((acc, p) => acc + sanitizeNumber(getProp(p, 'Jumlah', 'jumlah')), 0);
    
    const debt = Math.max(0, targetTotal - totalPaid);
    const monthsBehind = iuranValue > 0 ? Math.ceil(debt / iuranValue) : 0;
    
    return { debt, monthsBehind, totalPaid, targetTotal };
  }, [pemasukan]);

  const totalTunggakan = useMemo(() => {
    return siswa.reduce((acc, s) => acc + calculateDebtInfo(s).debt, 0);
  }, [siswa, calculateDebtInfo]);

  const filteredSiswa = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return siswa.filter(s => {
      const name = String(getProp(s, 'Nama', 'nama') || "").toLowerCase();
      const nis = String(getProp(s, 'NIS', 'nis') || "");
      return name.includes(query) || nis.includes(query);
    });
  }, [siswa, searchQuery]);

  const handleSubmit = async (type: 'Pemasukan' | 'Pengeluaran', formData: any) => {
    setIsSubmitting(true);
    const result = await apiService.submitTransaction(type, formData);
    if (result.success) {
      await loadData();
      showNotification(`${type} berhasil disimpan!`, 'success');
    } else {
      showNotification(`Gagal menyimpan ${type}.`, 'error');
    }
    setIsSubmitting(false);
  };

  const handleAddStudent = async (studentData: Student) => {
    setIsSubmitting(true);
    const result = await apiService.submitStudent(studentData);
    if (result.success) {
      await loadData();
      showNotification('Data siswa berhasil ditambahkan!', 'success');
    } else {
      showNotification('Gagal menambah siswa.', 'error');
    }
    setIsSubmitting(false);
  };

  const handleDelete = async (type: 'Pemasukan' | 'Pengeluaran', id: string) => {
    if (confirm(`Yakin ingin menghapus data ini?`)) {
      await apiService.deleteTransaction(type, id);
      await loadData();
      showNotification('Data berhasil dihapus dari sistem.', 'success');
    }
  };

  const generateAIInsight = async () => {
    setIsAiLoading(true);
    const combined: Transaction[] = [
      ...pemasukan.map(p => ({ 
        ...p, type: 'INCOME' as const, amount: sanitizeNumber(getProp(p, 'Jumlah', 'jumlah')), 
        description: `Iuran ${getProp(p, 'Nama_Siswa', 'nama_siswa', 'Nama')}`, category: 'Kas', recordedBy: 'Admin', Tanggal: getProp(p, 'Tanggal', 'tanggal')
      })),
      ...pengeluaran.map(e => ({ 
        ...e, type: 'EXPENSE' as const, amount: sanitizeNumber(getProp(e, 'Jumlah', 'jumlah')), 
        description: getProp(e, 'Keperluan', 'keperluan'), category: 'Ops', recordedBy: 'Admin', Tanggal: getProp(e, 'Tanggal', 'tanggal')
      }))
    ];
    const insight = await getFinancialInsights(combined);
    setAiInsight(insight);
    setIsAiLoading(false);
  };

  const chartData = [...pemasukan, ...pengeluaran]
    .sort((a, b) => new Date(getProp(a, 'Tanggal', 'tanggal')).getTime() - new Date(getProp(b, 'Tanggal', 'tanggal')).getTime())
    .slice(-15)
    .map(t => {
      const amt = sanitizeNumber(getProp(t, 'Jumlah', 'jumlah'));
      const isExpense = !!getProp(t, 'Keperluan', 'keperluan');
      return {
        name: new Date(getProp(t, 'Tanggal', 'tanggal')).toLocaleDateString('id-ID', { day: 'numeric', month: 'short' }),
        amount: isExpense ? -amt : amt
      };
    });

  return (
    <div className="min-h-screen flex flex-col font-sans text-slate-600 bg-slate-50/50">
      {/* 
        HEADER DESIGN BARU
        - Identitas Kelas
        - Profil Guru (Muhammad Wahyudi)
      */}
      <header className="fixed top-4 left-4 right-4 z-50">
        <div className="max-w-7xl mx-auto bg-white/80 backdrop-blur-xl border border-white/40 shadow-xl shadow-slate-200/50 rounded-2xl px-4 md:px-6 py-3 flex items-center justify-between animate-enter">
          
          {/* Logo & Teacher Info */}
          <div className="flex items-center gap-3 md:gap-4 overflow-hidden">
             <div className="w-10 h-10 md:w-12 md:h-12 bg-gradient-primary rounded-xl flex items-center justify-center shadow-lg shadow-indigo-500/20 text-white flex-shrink-0 animate-floating">
               <i className="fa-solid fa-building-columns text-lg md:text-xl"></i>
             </div>
             <div className="flex flex-col overflow-hidden justify-center">
               <h1 className="text-sm md:text-lg font-black text-slate-800 leading-tight whitespace-nowrap">SIP KAS XI-4</h1>
               <div className="flex items-center gap-1.5 mt-0.5 overflow-hidden">
                  <i className="fa-solid fa-user-tie text-[10px] text-indigo-500 bg-indigo-50 p-1 rounded-md flex-shrink-0"></i>
                  <p className="text-[9px] md:text-[10px] font-bold text-slate-500 uppercase tracking-wide truncate">
                     Wali Kelas: <span className="text-indigo-600">Muhammad Wahyudi</span>
                  </p>
               </div>
             </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
             <button 
               onClick={() => { loadData(); showNotification('Data disinkronisasi', 'success'); }}
               className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center bg-slate-50 hover:bg-indigo-50 text-slate-400 hover:text-indigo-600 rounded-xl transition-all border border-slate-100"
               title="Refresh Data"
             >
               <i className={`fa-solid fa-arrows-rotate ${isLoading ? 'animate-spin' : ''}`}></i>
             </button>

             {!currentUser ? (
               <button 
                onClick={() => setShowLoginModal(true)}
                className="flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white px-3 py-2 md:px-5 md:py-2.5 rounded-xl font-bold text-[10px] md:text-xs transition-all shadow-lg hover:shadow-xl active:scale-95"
               >
                 <i className="fa-solid fa-shield-halved"></i>
                 <span className="hidden sm:inline">ADMIN</span>
               </button>
             ) : (
               <div className="flex items-center gap-2 bg-indigo-50 pr-3 pl-1.5 py-1.5 rounded-full max-w-[140px] md:max-w-none">
                 <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center text-white text-xs font-bold shadow-md flex-shrink-0">
                    {currentUser.username.charAt(0)}
                 </div>
                 <div className="flex flex-col overflow-hidden">
                    <span className="text-[9px] font-bold text-indigo-900 uppercase leading-none truncate">{currentUser.username}</span>
                    <button onClick={handleLogout} className="text-[8px] text-indigo-400 font-bold uppercase text-left hover:text-indigo-600 whitespace-nowrap">Logout</button>
                 </div>
               </div>
             )}
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 w-full pt-28 md:pt-32 pb-20">
        
        {/* Welcome Hero - Clean & Data Centric */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8 md:mb-10">
          <div className="lg:col-span-2 neo-card p-8 md:p-10 relative overflow-hidden bg-gradient-primary text-white flex flex-col justify-center animate-enter delay-100 group">
             {/* Abstract Shapes */}
             <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
             <div className="absolute bottom-0 left-0 w-40 h-40 bg-indigo-400/30 rounded-full blur-2xl -translate-x-1/3 translate-y-1/3"></div>
             
             <div className="relative z-10">
               <div className="inline-block px-3 py-1 rounded-lg bg-white/20 backdrop-blur-md border border-white/10 text-[10px] font-bold uppercase tracking-widest mb-4">
                 <i className="fa-regular fa-chart-bar mr-1"></i> Laporan Keuangan Realtime
               </div>
               <h2 className="text-3xl md:text-4xl font-black mb-4 tracking-tight leading-tight">
                 Transparansi Keuangan <br/>
                 <span className="opacity-80 font-medium">Untuk Kelas Yang Lebih Maju.</span>
               </h2>
               <div className="flex items-center gap-8 mt-4">
                  <div>
                    <p className="text-3xl font-black">{pemasukan.length}</p>
                    <p className="text-[10px] uppercase font-bold opacity-60">Transaksi</p>
                  </div>
                  <div className="w-[1px] h-8 bg-white/20"></div>
                  <div>
                    <p className="text-3xl font-black">{siswa.length}</p>
                    <p className="text-[10px] uppercase font-bold opacity-60">Siswa Aktif</p>
                  </div>
               </div>
             </div>
             
             {/* Icon Decoration */}
             <div className="absolute right-6 top-1/2 -translate-y-1/2 hidden md:block opacity-20 group-hover:opacity-30 group-hover:scale-110 transition-all duration-700">
               <i className="fa-solid fa-wallet text-9xl"></i>
             </div>
          </div>

          <div className="neo-card p-6 flex flex-col justify-between animate-enter delay-200">
             <div>
                <h3 className="font-bold text-slate-800 text-lg mb-1">Status Kas</h3>
                <p className="text-xs text-slate-400">Update Terakhir: {new Date().toLocaleDateString('id-ID')}</p>
             </div>
             
             <div className="py-6 flex items-center justify-center">
                {summary.balance >= 0 ? (
                  <div className="relative">
                    <div className="w-32 h-32 rounded-full border-[6px] border-emerald-100 flex items-center justify-center">
                       <i className="fa-solid fa-check text-4xl text-emerald-500"></i>
                    </div>
                    <div className="absolute inset-0 border-[6px] border-emerald-500 rounded-full border-t-transparent animate-spin"></div>
                  </div>
                ) : (
                  <div className="w-32 h-32 rounded-full bg-rose-50 flex items-center justify-center">
                     <i className="fa-solid fa-exclamation text-4xl text-rose-500"></i>
                  </div>
                )}
             </div>

             <div className="text-center">
               <p className="text-sm font-bold text-slate-500 mb-1">Saldo Tersedia</p>
               <p className={`text-2xl font-black break-words ${summary.balance < 0 ? 'text-rose-500' : 'text-emerald-500'}`}>
                 Rp {summary.balance.toLocaleString('id-ID')}
               </p>
             </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-10">
           <div className="animate-enter delay-100"><StatCard title="Total Pemasukan" value={summary.totalIncome} type="income" /></div>
           <div className="animate-enter delay-200"><StatCard title="Total Pengeluaran" value={summary.totalExpense} type="expense" /></div>
           <div className="animate-enter delay-300"><StatCard title="Saldo Saat Ini" value={summary.balance} type="balance" /></div>
           
           <div className="neo-card p-6 border-l-4 border-amber-400 flex flex-col justify-between animate-enter delay-400 relative overflow-hidden">
              <div className="flex justify-between items-start z-10">
                 <div className="overflow-hidden">
                   <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 truncate">Total Tunggakan</p>
                   <p className="text-2xl font-black text-slate-800 break-words">Rp {totalTunggakan.toLocaleString('id-ID')}</p>
                 </div>
                 <div className="w-10 h-10 rounded-lg bg-amber-50 text-amber-500 flex items-center justify-center flex-shrink-0">
                    <i className="fa-solid fa-clock-rotate-left"></i>
                 </div>
              </div>
              {/* Background Icon */}
              <i className="fa-solid fa-triangle-exclamation absolute -bottom-4 -right-4 text-8xl text-amber-500/5 z-0"></i>
           </div>
        </div>

        {/* Info Board Section */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 animate-enter delay-300">
           <div className="neo-card p-6 border-t-4 border-t-indigo-400 md:border-t-0 md:border-l-4 md:border-l-indigo-400 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-3">
                 <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600 flex-shrink-0">
                    <i className="fa-regular fa-calendar-check"></i>
                 </div>
                 <h3 className="font-bold text-slate-800 text-sm">Jadwal Pembayaran</h3>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed font-medium">
                 Pembayaran uang kas dilakukan paling lambat <span className="text-indigo-600 font-bold">tanggal 10</span> setiap bulannya. Disiplin adalah kunci.
              </p>
           </div>
           
           <div className="neo-card p-6 border-t-4 border-t-emerald-400 md:border-t-0 md:border-l-4 md:border-l-emerald-400 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-3">
                 <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center text-emerald-600 flex-shrink-0">
                    <i className="fa-solid fa-money-bill-wave"></i>
                 </div>
                 <h3 className="font-bold text-slate-800 text-sm">Besaran Iuran</h3>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed font-medium">
                 Nominal wajib kas kelas adalah <span className="text-emerald-600 font-bold">Rp 20.000</span> per bulan per siswa. Gunakan uang pas.
              </p>
           </div>

           <div className="neo-card p-6 border-t-4 border-t-amber-400 md:border-t-0 md:border-l-4 md:border-l-amber-400 hover:shadow-lg transition-all">
              <div className="flex items-center gap-4 mb-3">
                 <div className="w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center text-amber-600 flex-shrink-0">
                    <i className="fa-solid fa-circle-info"></i>
                 </div>
                 <h3 className="font-bold text-slate-800 text-sm">Pusat Informasi</h3>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed font-medium">
                 Jika ada kesalahan data atau kendala pembayaran, segera hubungi <span className="text-amber-600 font-bold">Bendahara Kelas</span>.
              </p>
           </div>
        </div>

        {/* Charts Section */}
        <div className="mb-10 animate-enter delay-300">
           <div className="neo-card p-6 md:p-8 h-[350px]">
              <div className="flex items-center justify-between mb-6">
                 <div>
                    <h3 className="font-bold text-slate-800 text-lg">Tren Arus Kas</h3>
                    <p className="text-xs text-slate-400">Visualisasi data 15 transaksi terakhir</p>
                 </div>
                 <div className="p-2 bg-slate-50 rounded-lg hidden sm:block">
                    <i className="fa-solid fa-chart-area text-slate-400"></i>
                 </div>
              </div>
              <div className="w-full h-[240px]">
                 <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData}>
                      <defs>
                        <linearGradient id="colorAmt" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                          <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e2e8f0" />
                      <XAxis dataKey="name" fontSize={11} tickLine={false} axisLine={false} stroke="#94a3b8" dy={10} minTickGap={20} />
                      <YAxis hide />
                      <Tooltip 
                        contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)', padding: '12px', background: '#fff' }}
                        itemStyle={{ fontWeight: 700, color: '#4f46e5', fontSize: '12px' }}
                        formatter={(value: number) => `Rp ${value.toLocaleString('id-ID')}`}
                      />
                      <Area type="monotone" dataKey="amount" stroke="#4f46e5" strokeWidth={3} fillOpacity={1} fill="url(#colorAmt)" activeDot={{ r: 6, strokeWidth: 0, fill: '#4338ca' }} />
                    </AreaChart>
                  </ResponsiveContainer>
              </div>
           </div>
        </div>

        {/* Main Interface Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
           
           {/* Left Column: Admin & Transaction List */}
           <div className="lg:col-span-8 space-y-8 animate-enter delay-400">
              {currentUser && (
                <div className="neo-card p-6 relative overflow-hidden border border-indigo-100">
                   <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500"></div>
                   <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
                      <div className="flex items-center gap-3">
                         <div className="w-10 h-10 rounded-full bg-indigo-50 flex items-center justify-center">
                            <i className="fa-solid fa-sliders text-indigo-600"></i>
                         </div>
                         <h3 className="font-bold text-slate-800">Panel Admin</h3>
                      </div>
                      <div className="flex bg-slate-100 p-1 rounded-lg w-full sm:w-auto">
                         <button onClick={() => setActiveAdminTab('TRANSACTION')} className={`flex-1 sm:flex-none px-4 py-1.5 rounded-md text-xs font-bold transition-all ${activeAdminTab === 'TRANSACTION' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'}`}>Transaksi</button>
                         <button onClick={() => setActiveAdminTab('STUDENT')} className={`flex-1 sm:flex-none px-4 py-1.5 rounded-md text-xs font-bold transition-all ${activeAdminTab === 'STUDENT' ? 'bg-white shadow-sm text-indigo-600' : 'text-slate-400'}`}>Siswa</button>
                      </div>
                   </div>

                   {activeAdminTab === 'TRANSACTION' ? (
                      <TransactionForm students={siswa} onSubmit={handleSubmit} isLoading={isSubmitting} />
                    ) : (
                      <StudentForm onSubmit={handleAddStudent} onCancel={() => setActiveAdminTab('TRANSACTION')} isLoading={isSubmitting} />
                    )}
                </div>
              )}

              <div className="neo-card overflow-hidden min-h-[500px]">
                 <TransactionList 
                    pemasukan={pemasukan} 
                    pengeluaran={pengeluaran} 
                    onDelete={handleDelete} 
                    isAdmin={!!currentUser} 
                 />
              </div>
           </div>

           {/* Right Column: AI & Students */}
           <div className="lg:col-span-4 space-y-8 animate-enter delay-500">
              
              {/* AI Insight Card - Only for Admin/Bendahara */}
              {currentUser && (
                <div className="neo-card bg-slate-900 text-white p-6 relative overflow-hidden group">
                   <div className="absolute -right-10 -top-10 w-40 h-40 bg-indigo-500 rounded-full blur-3xl opacity-30 group-hover:opacity-50 transition-opacity duration-700"></div>
                   
                   <div className="relative z-10">
                      <div className="flex items-center gap-3 mb-4">
                         <i className="fa-solid fa-robot text-indigo-400 text-xl animate-bounce"></i>
                         <h3 className="font-bold text-lg">AI Advisor</h3>
                      </div>
                      
                      <div className="bg-white/5 rounded-xl p-4 mb-4 min-h-[150px] max-h-[300px] overflow-y-auto custom-scrollbar border border-white/10">
                         {aiInsight ? (
                             aiInsight.split('\n').map((line, i) => <p key={i} className="mb-2 text-xs leading-relaxed opacity-80 font-light">{line}</p>)
                           ) : (
                             <div className="text-center opacity-50 py-8">
                                <p className="text-xs italic">"Klik tombol di bawah untuk analisis cerdas."</p>
                             </div>
                           )}
                      </div>

                      <button 
                         onClick={generateAIInsight}
                         disabled={isAiLoading}
                         className="w-full py-3 bg-indigo-600 hover:bg-indigo-500 rounded-xl text-xs font-bold uppercase tracking-wider transition-all shadow-lg shadow-indigo-900/50 flex items-center justify-center gap-2"
                      >
                         {isAiLoading ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <><i className="fa-solid fa-wand-magic-sparkles"></i> Analisis Data</>}
                      </button>
                   </div>
                </div>
              )}

              {/* Student List - Vertical Scroll */}
              <div className="neo-card p-6 h-[600px] flex flex-col">
                 <div className="flex items-center justify-between mb-4">
                    <h3 className="font-bold text-slate-800">Daftar Siswa</h3>
                    <span className="bg-slate-100 text-slate-600 text-[10px] font-bold px-2 py-1 rounded-md">{siswa.length} Total</span>
                 </div>
                 
                 <div className="relative mb-4">
                    <input 
                      type="text" 
                      placeholder="Cari nama..." 
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl px-4 py-3 pl-10 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-3.5 text-slate-400 text-sm"></i>
                 </div>

                 <div className="flex-1 overflow-y-auto custom-scrollbar space-y-2 pr-1">
                    {filteredSiswa.map((s, idx) => {
                       const debtInfo = calculateDebtInfo(s);
                       return (
                          <div key={idx} className="flex items-center justify-between p-3 hover:bg-slate-50 rounded-xl transition-colors cursor-default border border-transparent hover:border-slate-100">
                             <div className="flex items-center gap-3 overflow-hidden">
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center text-xs font-bold ${s.JK === 'L' ? 'bg-indigo-50 text-indigo-600' : 'bg-rose-50 text-rose-600'} flex-shrink-0`}>
                                   {s.Nama.charAt(0)}
                                </div>
                                <div className="overflow-hidden">
                                   <p className="text-xs font-bold text-slate-700 truncate w-28 sm:w-32">{s.Nama}</p>
                                   <p className="text-[10px] text-slate-400">{s.NIS}</p>
                                </div>
                             </div>
                             <div className="flex-shrink-0">
                                {debtInfo.debt <= 0 ? (
                                   <span className="text-[9px] font-bold text-emerald-500 bg-emerald-50 px-2 py-0.5 rounded-md">Lunas</span>
                                ) : (
                                   <span className="text-[9px] font-bold text-rose-500 bg-rose-50 px-2 py-0.5 rounded-md">-{debtInfo.monthsBehind} Bln</span>
                                )}
                             </div>
                          </div>
                       )
                    })}
                 </div>
              </div>

           </div>
        </div>

      </main>

      {/* Login Modal */}
      {showLoginModal && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-md animate-enter">
           <div className="w-full max-w-sm relative" onClick={(e) => e.stopPropagation()}>
             <button onClick={() => setShowLoginModal(false)} className="absolute -top-12 right-0 text-white hover:text-rose-300 transition-colors">
               <i className="fa-solid fa-xmark text-3xl"></i>
             </button>
             <LoginForm onLogin={handleLogin} isLoading={isLoggingIn} error={loginError} />
           </div>
           <div className="absolute inset-0 -z-10" onClick={() => setShowLoginModal(false)}></div>
        </div>
      )}

      {/* Footer Design Baru */}
      <footer className="bg-white border-t border-slate-200 mt-auto py-8">
        <div className="max-w-7xl mx-auto px-4 flex flex-col items-center justify-center">
           <div className="mb-3 opacity-50 grayscale hover:grayscale-0 transition-all duration-500">
              <img src="https://cdn-icons-png.flaticon.com/512/2845/2845812.png" alt="Logo" className="w-8 h-8" />
           </div>
           <p className="text-slate-400 text-[11px] font-bold uppercase tracking-[0.15em] text-center">
             &copy; 2026 • Dikelola oleh Bendahara Kelas
           </p>
        </div>
      </footer>

      {/* Notification Toast */}
      {notification && (
        <div className="fixed bottom-8 right-4 z-[100] animate-enter">
           <div className={`px-4 py-3 rounded-xl shadow-2xl flex items-center gap-3 border ${notification.type === 'success' ? 'bg-indigo-900 text-white border-indigo-700' : 'bg-rose-600 text-white border-rose-500'}`}>
              <i className={`fa-solid ${notification.type === 'success' ? 'fa-circle-check' : 'fa-circle-exclamation'}`}></i>
              <p className="text-xs font-bold">{notification.message}</p>
           </div>
        </div>
      )}

    </div>
  );
};

export default App;
